import { useExcalidrawAPI } from "@excalidraw/excalidraw";
import { FilledButton } from "@excalidraw/excalidraw/components/FilledButton";
import { useCallback, useEffect, useState } from "react";

import { useAtomValue, useSetAtom } from "../../app-jotai";
import {
  activeSceneAtom,
  authDialogAtom,
  currentUserAtom,
  workspaceApi,
  type WorkspaceComment,
} from "../../data/workspace";

import "./Workspace.scss";

export const CommentsPanel = () => {
  const excalidrawAPI = useExcalidrawAPI();
  const currentUser = useAtomValue(currentUserAtom);
  const activeScene = useAtomValue(activeSceneAtom);
  const openAuth = useSetAtom(authDialogAtom);

  const [comments, setComments] = useState<WorkspaceComment[]>([]);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  const sceneId = activeScene?.id;

  const refresh = useCallback(() => {
    if (!sceneId) {
      setComments([]);
      return;
    }
    workspaceApi
      .listComments(sceneId)
      .then((r) => setComments(r.comments))
      .catch((e) => setError(e.message));
  }, [sceneId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!currentUser) {
    return (
      <div className="WorkspacePanel">
        <div className="WorkspacePanel__title">Comments</div>
        <p className="WorkspacePanel__signin">
          Sign in to leave comments on your cloud scenes.
        </p>
        <FilledButton
          size="medium"
          label="Sign in"
          onClick={() => openAuth(true)}
        />
      </div>
    );
  }

  if (!activeScene) {
    return (
      <div className="WorkspacePanel">
        <div className="WorkspacePanel__title">Comments</div>
        <p className="WorkspacePanel__hint">
          Open or save a scene to your workspace (My scenes) to start
          commenting.
        </p>
      </div>
    );
  }

  const addComment = async () => {
    if (!draft.trim() || !sceneId) {
      return;
    }
    // Anchor the comment at the current viewport centre (scene coordinates).
    let x = 0;
    let y = 0;
    if (excalidrawAPI) {
      const st = excalidrawAPI.getAppState();
      x = st.width / 2 / st.zoom.value - st.scrollX;
      y = st.height / 2 / st.zoom.value - st.scrollY;
    }
    try {
      await workspaceApi.createComment(sceneId, { x, y, text: draft.trim() });
      setDraft("");
      refresh();
    } catch (e: any) {
      setError(e.message);
    }
  };

  const toggleResolved = async (c: WorkspaceComment) => {
    await workspaceApi.updateComment(c.id, { resolved: !c.resolved });
    refresh();
  };

  const remove = async (c: WorkspaceComment) => {
    await workspaceApi.deleteComment(c.id);
    refresh();
  };

  const locate = (c: WorkspaceComment) => {
    excalidrawAPI?.setViewport({
      target: [c.x - 150, c.y - 150, c.x + 150, c.y + 150],
      fit: "none",
      animation: true,
    });
  };

  return (
    <div className="WorkspacePanel">
      <div className="WorkspacePanel__title">Comments</div>
      <div className="WorkspaceComposer">
        <textarea
          value={draft}
          placeholder={`Comment on “${activeScene.name}”…`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              addComment();
            }
          }}
        />
        <FilledButton
          size="medium"
          label="Comment"
          onClick={addComment}
          disabled={!draft.trim()}
        />
      </div>

      {error && <p className="Workspace__error">{error}</p>}

      {comments.length === 0 ? (
        <p className="WorkspacePanel__hint">No comments yet.</p>
      ) : (
        <div
          style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}
        >
          {comments.map((c) => (
            <div
              key={c.id}
              className={`WorkspaceComment ${
                c.resolved ? "WorkspaceComment--resolved" : ""
              }`}
            >
              <div className="WorkspaceComment__head">
                <span className="WorkspaceComment__author">{c.author}</span>
                <span className="WorkspaceComment__date">
                  {new Date(c.createdAt).toLocaleString()}
                </span>
              </div>
              <div className="WorkspaceComment__text">{c.text}</div>
              <div className="WorkspaceComment__actions">
                <button type="button" onClick={() => locate(c)}>
                  Locate
                </button>
                <button type="button" onClick={() => toggleResolved(c)}>
                  {c.resolved ? "Reopen" : "Resolve"}
                </button>
                <button type="button" onClick={() => remove(c)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
